import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { credentialsApi } from '@/api/credentials.api';
import { extractErrorMessage } from '@/lib/errors';

export function useCredentialsList() {
  return useQuery({
    queryKey: ['credentials'],
    queryFn: credentialsApi.list,
    select: (res) => res.data.data,
  });
}

export function useCreateCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: credentialsApi.create,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['credentials'] });
      toast.success('Credencial criada com sucesso');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useUpdateCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof credentialsApi.update>[1] }) =>
      credentialsApi.update(id, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['credentials'] });
      toast.success('Credencial atualizada');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useDeleteCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: credentialsApi.delete,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['credentials'] });
      toast.success('Credencial removida');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useTestCredential() {
  return useMutation({
    mutationFn: (id: string) => credentialsApi.test(id),
    onSuccess: (res) => {
      const result = res.data.data;
      if (result.success) {
        toast.success(`Credencial válida — ${result.templatesFound ?? 0} template(s) encontrado(s)`);
      } else {
        toast.error(`Credencial inválida: ${result.error ?? 'Erro desconhecido'}`);
      }
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

export function useRegisterWebhook() {
  return useMutation({
    mutationFn: (id: string) => credentialsApi.registerWebhook(id),
    onSuccess: (res) => {
      const { action, webhookUrl } = res.data.data;
      if (action === 'already_registered') {
        toast.info(`Webhook já registrado: ${webhookUrl}`);
      } else {
        toast.success(`Webhook registrado com sucesso`);
      }
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}
